// Provider global pour WebSocket + Notifications
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useWebSocket } from "@/lib/useWebSocket";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const RealtimeContext = createContext(null);

// Petit son "ding" base64 (très court)
const NOTIFY_SOUND_BASE64 = "data:audio/wav;base64,UklGRl4DAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YToDAAAA////////////////////////";

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const { connected, subscribe } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [dataChangeCounter, setDataChangeCounter] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // S'abonner aux messages WebSocket
  useEffect(() => {
    const unsubscribe = subscribe((msg) => {
      if (msg.type === "notification") {
        setNotifications((n) => [msg.notification, ...n].slice(0, 100));
        setUnread((u) => u + 1);
        // Play sound
        try {
          const audio = new Audio(NOTIFY_SOUND_BASE64);
          audio.volume = 0.4;
          audio.play().catch(() => {});
        } catch {}
        // Native browser notification
        if (window.Notification?.permission === "granted") {
          try { new Notification(msg.notification.title, { body: msg.notification.body, icon: "/favicon.ico" }); } catch {}
        }
      } else if (msg.type === "data_change") {
        // Increment counter so subscribed pages can reload
        setDataChangeCounter((c) => c + 1);
        setLastEvent(msg);
      }
    });
    return unsubscribe;
  }, [subscribe]);

  // Demander permission notifications natives
  useEffect(() => {
    if (user && window.Notification && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, [user]);

  const markRead = useCallback(async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      setNotifications((arr) => arr.map((n) => n.id === nid ? { ...n, read_by: [...(n.read_by || []), user?.id] } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch {}
  }, [user]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((arr) => arr.map((n) => ({ ...n, read_by: [...(n.read_by || []), user?.id] })));
      setUnread(0);
    } catch {}
  }, [user]);

  return (
    <RealtimeContext.Provider value={{
      connected,
      subscribe,
      notifications,
      unread,
      refresh,
      markRead,
      markAllRead,
      dataChangeCounter,
      lastEvent,
    }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
